import axios from 'axios';

// Public API client (no auth required)
const publicClient = axios.create({
  baseURL: '/api/v1/public',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types for public landing page data
export interface PublicOwner {
  firstName: string;
  lastName: string;
  avatar?: string;
  avatarColor: string;
}

export interface PublicLandingPage {
  headline: string;
  subheadline: string;
  bio: string;
  contactEmail: string;
  phone: string;
  website: string;
  linkedIn: string;
  instagram: string;
  primaryColor: string;
  showServices: boolean;
}

export interface PublicLeadMagnet {
  enabled: boolean;
  headline: string;
  subheadline: string;
  buttonText: string;
}

export interface PublicService {
  _id: string;
  name: string;
  description?: string;
  duration: number;
  price: number;
  category?: string;
  color?: string;
}

export interface PublicLandingPageData {
  name: string;
  slug: string;
  owner: PublicOwner;
  landingPage: PublicLandingPage;
  leadMagnet: PublicLeadMagnet | null;
  services: PublicService[] | null;
}

export interface LeadSubmission {
  firstName: string;
  lastName?: string;
  email: string;
  challengeText: string;
}

export interface LeadSubmissionResponse {
  message: string;
  leadId: string;
}

// Types for public invoice receipt
export interface PublicReceiptLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface PublicReceipt {
  invoiceNumber: string;
  status: string;
  amount: number;
  currency: string;
  paidAt: string | null;
  issueDate: string;
  dueDate: string;
  lineItems: PublicReceiptLineItem[];
  subtotal: number;
  tax?: number;
  taxRate?: number;
  discount?: number;
  total: number;
  business: {
    name: string;
    email?: string;
  };
  client: {
    name: string;
  };
  paymentTerms?: string;
}

export const publicApi = {
  /**
   * Get public landing page data by slug
   */
  getLandingPage: async (slug: string): Promise<PublicLandingPageData> => {
    const { data } = await publicClient.get(`/${slug}`);
    return data.data;
  },

  /**
   * Submit a lead through the lead magnet form
   */
  submitLead: async (slug: string, leadData: LeadSubmission): Promise<LeadSubmissionResponse> => {
    const { data } = await publicClient.post(`/${slug}/lead`, leadData);
    return data.data;
  },

  /**
   * Get public receipt for a paid invoice
   */
  getReceipt: async (invoiceId: string): Promise<PublicReceipt> => {
    const { data } = await publicClient.get(`/invoices/${invoiceId}/receipt`);
    return data.data;
  },

  /**
   * Get the URL for downloading a receipt PDF
   */
  getReceiptPdfUrl: (invoiceId: string): string => {
    return `/api/v1/public/invoices/${invoiceId}/receipt/pdf`;
  },
};

// Helper to get error message from public API errors
export function getPublicApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data;
    return apiError?.error?.message || error.message || 'An unexpected error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
